import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import Select, { components, MenuListProps } from 'react-select'
import { useAppDispatch } from 'src/redux/storeHooks'
import { getOmsPlatformFieldDataOptionsAsync } from 'src/redux/actions/omsPageActions'
import { PlatformFieldDataSelectOption } from './types'
import {extractPlatformFieldDataResults, toPlatformFieldDataOption} from './platformFieldHelpers'

type OmsPlatformFieldDataSelectProps = {
  /** Resolved native platform field id used by /api/platform-field-data/{id} */
  fieldId: number | null
  value: string
  onChange: (val: string, label: string, raw?: any) => void
  placeholder?: string
  disabled?: boolean
  isDisabled?: boolean
  loading?: boolean
  seedOption?: PlatformFieldDataSelectOption | null
}

const SEARCH_DEBOUNCE_MS = 400

const isAbortedRequest = (error: any): boolean =>
  error?.name === 'AbortError' ||
  error?.name === 'CanceledError' ||
  error?.code === 'ERR_CANCELED'

const getNextUrl = (payload: any): string | null => {
  const next = payload?.next ?? payload?.data?.next
  return typeof next === 'string' && next.length > 0 ? next : null
}

const mergeUniqueOptions = (options: PlatformFieldDataSelectOption[]): PlatformFieldDataSelectOption[] => {
  const seen = new Set<string>()
  return options.filter((opt) => {
    if (seen.has(opt.value)) return false
    seen.add(opt.value)
    return true
  })
}

// ── Paginated Menu List (scroll position preservation) ──

type ScrollInternals = {
  menuListNodeRef: React.MutableRefObject<HTMLDivElement | null>
  savedScrollTopRef: React.MutableRefObject<number>
  pendingScrollRestoreRef: React.MutableRefObject<number | null>
  onMenuScroll: (event: React.UIEvent<HTMLDivElement>) => void
}

const ScrollInternalsContext = createContext<ScrollInternals | null>(null)

const PaginatedMenuList = (props: MenuListProps<PlatformFieldDataSelectOption, false>) => {
  const internals = useContext(ScrollInternalsContext)
  const { ref: menuRef, ...menuInnerProps } = props.innerProps

  useLayoutEffect(() => {
    if (!internals || internals.pendingScrollRestoreRef.current === null) return
    const scrollTop = internals.pendingScrollRestoreRef.current
    const menuNode = internals.menuListNodeRef.current
    if (!menuNode) return
    menuNode.scrollTop = scrollTop
    internals.savedScrollTopRef.current = scrollTop
    if (Math.abs(menuNode.scrollTop - scrollTop) < 2) {
      internals.pendingScrollRestoreRef.current = null
    }
  }, [internals, props.children])

  return (
    <components.MenuList
      {...props}
      innerProps={{
        ...menuInnerProps,
        ref: (node: HTMLDivElement | null) => {
          if (internals) internals.menuListNodeRef.current = node
          if (typeof menuRef === 'function') menuRef(node)
          else if (menuRef && typeof menuRef === 'object') {
            ;(menuRef as React.MutableRefObject<HTMLDivElement | null>).current = node
          }
        },
        onScroll: (event) => {
          if (internals) internals.savedScrollTopRef.current = event.currentTarget.scrollTop
          menuInnerProps?.onScroll?.(event)
          if (internals) internals.onMenuScroll(event as React.UIEvent<HTMLDivElement>)
        },
      }}
    >
      {props.children}
    </components.MenuList>
  )
}

// Appearance is driven entirely by the scoped `.oms-rs__*` rules in oms-order.css
// (classNamePrefix="oms-rs") so all order-page selects share one design system.

const OmsPlatformFieldDataSelect: FC<OmsPlatformFieldDataSelectProps> = ({
  fieldId,
  value,
  onChange,
  placeholder = 'Search and select...',
  disabled = false,
  isDisabled = false,
  loading = false,
  seedOption = null,
}) => {
  const dispatch = useAppDispatch()
  const [options, setOptions] = useState<PlatformFieldDataSelectOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const searchAbortRef = useRef<AbortController | null>(null)
  const paginationAbortRef = useRef<AbortController | null>(null)
  const activeSearchRef = useRef('')
  const nextUrlRef = useRef<string | null>(null)
  const isLoadingMoreRef = useRef(false)
  const menuListNodeRef = useRef<HTMLDivElement | null>(null)
  const savedScrollTopRef = useRef(0)
  const pendingScrollRestoreRef = useRef<number | null>(null)
  const initialFetchDoneRef = useRef(false)
  const lastAutoCorrectedValueRef = useRef<string | null>(null)
  const onChangeRef = useRef(onChange)

  useLayoutEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  // Fetch initial options whenever the resolved platform field id changes.
  useEffect(() => {
    setOptions(seedOption ? [seedOption] : [])
    setInputValue('')
    nextUrlRef.current = null
    initialFetchDoneRef.current = false

    if (!fieldId) return
    initialFetchDoneRef.current = true
    fetchOptions('')
  }, [fieldId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      cancelPendingSearch()
      paginationAbortRef.current?.abort()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const cancelPendingSearch = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = null
    }
    searchAbortRef.current?.abort()
    searchAbortRef.current = null
  }, [])

  const captureScrollPosition = useCallback(() => {
    const scrollTop = menuListNodeRef.current?.scrollTop ?? savedScrollTopRef.current
    if (scrollTop > 0) {
      savedScrollTopRef.current = scrollTop
      pendingScrollRestoreRef.current = scrollTop
    }
    return scrollTop
  }, [])

  const fetchOptions = useCallback(
    async (searchString: string) => {
      if (!fieldId) return

      const abortController = new AbortController()
      cancelPendingSearch()
      paginationAbortRef.current?.abort()
      paginationAbortRef.current = null
      searchAbortRef.current = abortController
      activeSearchRef.current = searchString.trim()

      setIsLoading(true)

      try {
        const response = await dispatch(
          getOmsPlatformFieldDataOptionsAsync({
            fieldId,
            search_string: searchString.trim(),
            signal: abortController.signal,
          })
        )

        if (abortController.signal.aborted) return

        if (getOmsPlatformFieldDataOptionsAsync.rejected.match(response)) {
          if (isAbortedRequest(response.error) || isAbortedRequest(response.payload)) return
        }

        const results = extractPlatformFieldDataResults(response.payload)
        const newOptions = results.map(toPlatformFieldDataOption).filter(Boolean) as PlatformFieldDataSelectOption[]
        let merged = mergeUniqueOptions(newOptions)
        if (seedOption?.value && !merged.some((opt) => opt.value === seedOption.value)) {
          merged = mergeUniqueOptions([seedOption, ...merged])
        }
        setOptions(merged)
        nextUrlRef.current = getNextUrl(response.payload)
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false)
        }
      }
    },
    [cancelPendingSearch, dispatch, fieldId, seedOption]
  )

  const loadMoreOptions = useCallback(async () => {
    const currentNextUrl = nextUrlRef.current
    if (!currentNextUrl || isLoadingMoreRef.current) return

    const abortController = new AbortController()
    paginationAbortRef.current?.abort()
    paginationAbortRef.current = abortController
    isLoadingMoreRef.current = true
    captureScrollPosition()

    try {
      const response = await dispatch(
        getOmsPlatformFieldDataOptionsAsync({
          next_url: currentNextUrl,
          signal: abortController.signal,
        })
      )

      if (abortController.signal.aborted) return

      if (getOmsPlatformFieldDataOptionsAsync.rejected.match(response)) {
        if (isAbortedRequest(response.error) || isAbortedRequest(response.payload)) return
      }

      const results = extractPlatformFieldDataResults(response.payload)
      const moreOptions = results.map(toPlatformFieldDataOption).filter(Boolean) as PlatformFieldDataSelectOption[]
      setOptions((prev) => mergeUniqueOptions([...prev, ...moreOptions]))
      nextUrlRef.current = getNextUrl(response.payload)
    } finally {
      if (!abortController.signal.aborted) {
        isLoadingMoreRef.current = false
      }
    }
  }, [captureScrollPosition, dispatch])

  const handleMenuScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget
      savedScrollTopRef.current = target.scrollTop
      const isNearBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 24
      if (isNearBottom) {
        captureScrollPosition()
        loadMoreOptions()
      }
    },
    [captureScrollPosition, loadMoreOptions]
  )

  const scrollInternals = useMemo<ScrollInternals>(
    () => ({
      menuListNodeRef,
      savedScrollTopRef,
      pendingScrollRestoreRef,
      onMenuScroll: handleMenuScroll,
    }),
    [handleMenuScroll]
  )

  const handleInputChange = (nextInputValue: string, actionMeta: { action: string }) => {
    if (actionMeta.action !== 'input-change') return

    setInputValue(nextInputValue)
    activeSearchRef.current = nextInputValue.trim()
    cancelPendingSearch()
    paginationAbortRef.current?.abort()
    paginationAbortRef.current = null

    searchTimeoutRef.current = setTimeout(() => {
      fetchOptions(nextInputValue.trim())
    }, SEARCH_DEBOUNCE_MS)
  }

  const selectedValue = useMemo((): PlatformFieldDataSelectOption | null => {
    if (!value) return null
    const matched = options.find(
      (opt) => opt.value === value || opt.raw?.api_id === value || opt.raw?.api_name === value
    )
    if (matched) return matched
    if (seedOption?.value === value && seedOption.label) return seedOption
    if (seedOption?.label === value) return seedOption
    return {value, label: value}
  }, [value, options, seedOption])

  // Auto-correct string values (like "PROCESSING") to numeric IDs once the option loads
  useEffect(() => {
    if (!value) return
    if (lastAutoCorrectedValueRef.current === value) return
    const matched = options.find(
      (opt) => opt.raw?.api_id === value || opt.raw?.api_name === value
    )
    if (matched && matched.value !== value) {
      lastAutoCorrectedValueRef.current = value
      onChangeRef.current(matched.value, matched.label, matched.raw)
    }
  }, [value, options]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (selected: PlatformFieldDataSelectOption | null) => {
    onChangeRef.current(selected?.value || '', selected?.label || '', selected?.raw)
    setInputValue('')
  }

  // Paginate until the saved platform_field_data id appears in options (hydration on edit)
  useEffect(() => {
    if (!value || !fieldId || isLoading || isLoadingMoreRef.current) return
    if (options.some((opt) => opt.value === value || opt.raw?.api_id === value || opt.raw?.api_name === value)) return
    if (nextUrlRef.current) {
      loadMoreOptions()
    }
  }, [value, options, fieldId, isLoading, loadMoreOptions])

  return (
    <ScrollInternalsContext.Provider value={scrollInternals}>
      <Select<PlatformFieldDataSelectOption, false>
        classNamePrefix='oms-rs'
        captureMenuScroll={false}
        components={{ MenuList: PaginatedMenuList }}
        filterOption={null}
        inputValue={inputValue}
        isClearable
        isDisabled={disabled || isDisabled || loading || !fieldId}
        isLoading={isLoading || loading}
        isSearchable
        menuPlacement='auto'
        maxMenuHeight={260}
        menuShouldScrollIntoView={false}
        noOptionsMessage={() => (isLoading ? 'Loading...' : 'No options found')}
        options={options}
        placeholder={placeholder}
        value={selectedValue}
        onChange={handleChange}
        onInputChange={handleInputChange}
        onMenuScrollToBottom={loadMoreOptions}
      />
    </ScrollInternalsContext.Provider>
  )
}

export default OmsPlatformFieldDataSelect
